import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, Package, Inbox } from 'lucide-react';
import toast from 'react-hot-toast';
import { pluginAPI } from '../utils/api';
import UserPluginCard from '../components/UserPluginCard';
import Pagination from '../components/Pagination';

const UserDownloads = () => {
  const { t } = useTranslation();
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 12;
  const [downloadingId, setDownloadingId] = useState(null);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  const fetchPlugins = useCallback(
    async (searchTerm = '', currentPage = page) => {
      try {
        setLoading(true);
        const params = { page: currentPage, limit };
        if (searchTerm) params.search = searchTerm;
        const response = await pluginAPI.list(params);
        setPlugins(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.total || 0);
      } catch (error) {
        toast.error(t('errors.serverError'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page]
  );

  useEffect(() => {
    fetchPlugins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchPlugins(searchQuery, 1);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    fetchPlugins(searchQuery, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleDownload = async (plugin) => {
    setDownloadingId(plugin._id);
    let url = null;
    try {
      const response = await pluginAPI.download(plugin._id);
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', plugin.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('plugins.downloadSuccess'));
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      if (url) window.URL.revokeObjectURL(url);
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('sidebar.user.downloads')}</h1>
        <p className="text-gray-500 text-sm mt-1">{t('userDownloads.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('userDownloads.searchPlaceholder')}
              className="form-input pl-10 w-full"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <Package className="w-4 h-4" />
          <span>
            {t('plugins.totalCount')}: <strong className="text-gray-900">{total}</strong> {t('plugins.plugin')}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
          <p className="text-gray-500 mt-2">{t('common.loading')}</p>
        </div>
      ) : plugins.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-600">{t('plugins.emptyTitle')}</p>
          <p className="text-sm mt-1 text-gray-400">{t('plugins.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {plugins.map((p) => (
              <UserPluginCard
                key={p._id}
                plugin={p}
                onDownload={handleDownload}
                downloading={downloadingId === p._id}
              />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default UserDownloads;
